import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { Target, Heart, Users, Award, Cpu, ArrowRight, CheckCircle2 } from 'lucide-react';
import { siteImages } from '../data/mock';

const OurMissionPage = () => {
  useEffect(() => { document.title = 'Our Mission | Desk Line Supply'; }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main>
        <section className="relative h-64 md:h-80 overflow-hidden bg-dark-900">
          <img src={siteImages.heroLifestyle} alt="Our Mission" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-900 via-dark-900/85 to-dark-900/40" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 rounded-full text-athletic-300 text-xs font-semibold mb-4">
                <Target className="w-3.5 h-3.5" /> OUR PURPOSE
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white font-bold mb-3">Our Mission</h1>
              <p className="text-dark-200 max-w-lg">Making quality office supplies simple to find and love.</p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-dark-50 border border-dark-100 rounded-3xl p-8 md:p-12 mb-16 text-center">
            <div className="w-20 h-20 bg-athletic-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Cpu className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-display text-3xl lg:text-4xl text-dark-900 font-bold mb-6">Quality Office Supplies, Made Simple</h2>
            <p className="text-dark-600 text-lg leading-relaxed max-w-3xl mx-auto">
              At Desk Line Supply, our mission is simple: to make it effortless to discover supplies that genuinely improve your workday. We skip the overwhelming aisles and hand-pick a compact lineup of essentials we truly believe in &mdash; then back each one with fast shipping and friendly support.
            </p>
          </div>

          <div className="mb-16">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-athletic-50 text-athletic-700 text-xs font-bold uppercase tracking-wider rounded-full mb-4">Core Values</span>
              <h2 className="font-display text-3xl lg:text-4xl text-dark-900 font-bold">What we stand for</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Excellence', desc: 'We aim for excellence in every product we curate and every customer we help.' },
                { icon: Heart, title: 'Passion', desc: 'We genuinely love great supplies and the everyday focus they make possible.' },
                { icon: Users, title: 'Community', desc: 'We build a community of riders who inspire and inform each other.' },
                { icon: Award, title: 'Quality', desc: 'We never compromise — every product meets our bar for performance and value.' },
              ].map((value, i) => (
                <div key={i} className="bg-dark-50 border border-dark-100 rounded-2xl p-6 hover:border-athletic-300 transition-all group">
                  <div className="w-14 h-14 bg-athletic-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-athletic-100 transition-colors">
                    <value.icon className="w-7 h-7 text-athletic-700" />
                  </div>
                  <h3 className="font-display font-bold text-dark-900 text-lg mb-2">{value.title}</h3>
                  <p className="text-dark-500 text-sm leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <div className="aspect-video rounded-3xl overflow-hidden border border-dark-100">
                <img src={siteImages.powerBackground} alt="Our Promise" className="w-full h-full object-cover" />
              </div>
            </div>
            <div>
              <span className="inline-block px-4 py-1.5 bg-athletic-50 text-athletic-700 text-xs font-bold uppercase tracking-wider rounded-full mb-4">Our Promise</span>
              <h2 className="font-display text-3xl text-dark-900 font-bold mb-6">We&apos;re here for your setup</h2>
              <p className="text-dark-600 leading-relaxed mb-6">
                Whether you&apos;re stocking a first desk or upgrading a whole office, we&apos;re here to help. Our team is dedicated to matching you with the right supplies for the way you work &mdash; no big-box confusion required.
              </p>
              <div className="space-y-4">
                {['Handpicked office supplies and essentials', 'Fast free shipping across the USA', '30-day hassle-free returns', 'Dedicated customer support'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-athletic-600 flex-shrink-0" />
                    <span className="text-dark-900">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center bg-gradient-to-br from-athletic-50 to-power-50 border border-athletic-100 rounded-3xl p-12">
            <h2 className="font-display text-3xl font-bold text-dark-900 mb-4">Join the Desk Line family</h2>
            <p className="text-dark-600 mb-8 max-w-xl mx-auto">Upgrade your everyday with office essentials chosen for real work.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-4 bg-athletic-500 text-white font-bold uppercase tracking-wider rounded-lg hover:opacity-90 transition-all shadow-button">Shop Now <ArrowRight className="w-5 h-5" /></Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OurMissionPage;
